import React, { useState } from 'react';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { ClassroomDto } from '../../../../api-service/clients/auto-generated-clients';
import { ChangeCourseClassroomDialogComponent } from './change-course-classroom-dialog-component';
import { CourseBulkEditResultDialogComponent } from './course-bulk-edit-result-dialog-component';
import { courseResultSummary, ICourseBulkEditResult, skippedCourseItems, skippedCoursesText } from './bulk-edit-results';
import { useCompanyClassrooms } from './use-company-classrooms';

export interface IChangeCourseClassroomDialogProps {
  courseIds: string[];
  companyId: string;
  onCompleted?: () => void;
}

const attendanceClient = moduleClientFactory.createAttendanceClient();

export function ChangeCourseClassroomDialog({ courseIds, companyId, onCompleted }: IChangeCourseClassroomDialogProps) {
  const { classrooms, isLoadingClassrooms } = useCompanyClassrooms(companyId);
  const [isOpen, setIsOpen] = useState(true);
  const [classroomId, setClassroomId] = useState<string>();
  const [includeFutureLessons, setIncludeFutureLessons] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [result, setResult] = useState<ICourseBulkEditResult>();

  function handleClassroomChange(classroom?: ClassroomDto) {
    setClassroomId(classroom?.classroomId);
  }

  function handleIncludeFutureLessonsToggle() {
    setIncludeFutureLessons(!includeFutureLessons);
  }

  async function handleConfirm() {
    if (!classroomId) {
      return;
    }

    setIsSaving(true);
    try {
      const response = await attendanceClient.changeCourseClassroom({ courseIds, classroomId, includeFutureLessons });
      setResult(response);
    } catch {
      // The error message interceptor shows the reason, and the dialog stays open so the choice can be corrected
    } finally {
      setIsSaving(false);
    }
  }

  function handleClose() {
    setIsOpen(false);
  }

  function handleResultClose() {
    setIsOpen(false);
    onCompleted?.();
  }

  if (!isOpen) {
    return null;
  }

  if (result) {
    return (
      <CourseBulkEditResultDialogComponent
        title="Lokale er skiftet"
        summary={courseResultSummary(result, includeFutureLessons)}
        skippedItemsText={skippedCoursesText}
        skippedItems={skippedCourseItems(result)}
        onClose={handleResultClose}
      />
    );
  }

  return (
    <ChangeCourseClassroomDialogComponent
      title={courseIds.length === 1 ? 'Skift lokale på det valgte hold' : `Skift lokale på de ${courseIds.length} valgte hold`}
      classrooms={classrooms}
      classroomId={classroomId}
      futureLessonsOption={{
        label: 'Skift også lokale på holdenes fremtidige lektioner',
        description: 'Kun lektioner med holdets nuværende lokale får det nye lokale. Kladder kommer med. Aflyste lektioner og lektioner med låst protokol ændres ikke.',
        checked: includeFutureLessons,
        onToggle: handleIncludeFutureLessonsToggle,
      }}
      isLoadingClassrooms={isLoadingClassrooms}
      isSaving={isSaving}
      onClassroomChange={handleClassroomChange}
      onConfirm={handleConfirm}
      onClose={handleClose}
    />
  );
}
